import axios from 'axios';
import { msalConfig } from '../authConfig';
import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { getAccessToken } from './TokenService';

const backendBaseUrl = msalConfig.baseUrl;

// Interface for batch configuration based on API schema
export interface BatchConfiguration {
    id?: number;
    companyDataSourcesId: number;
    prompt1: string;
    prompt2: string;
    prompt3: string;
    status: string;   
    companyName: string;
    blobFileName?: string;
    createdDate?: string;
    updatedDate?: string;
}

// Interface for batch configuration request
export interface BatchConfigurationRequest {
    file: File;
    companyDataSourcesId: number;
    prompt1: string;
    prompt2: string;
    prompt3: string;
    status: string;
}

// Interface for paginated response
export interface PaginatedBatchConfigurationResponse {
    items: BatchConfiguration[];
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
}

// Interface for default templates response
export interface DefaultTemplatesResponse {
    prompt1: string;
    prompt2: string;
    prompt3: string;
}

// Get all batch configurations
export async function getBatchConfigurations(
    msalInstance: IPublicClientApplication, 
    account?: AccountInfo
): Promise<BatchConfiguration[]> {
    const token = await getAccessToken(msalInstance, account);
    const response = await axios.get(`${backendBaseUrl}api/BatchConfiguration`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data;
}

// Get paginated batch configurations
export async function getPaginatedBatchConfigurations(
    msalInstance: IPublicClientApplication,
    page: number = 1,
    pageSize: number = 10,
    account?: AccountInfo
): Promise<PaginatedBatchConfigurationResponse> {
    const token = await getAccessToken(msalInstance, account);
    const response = await axios.get(`${backendBaseUrl}api/BatchConfiguration/paginated`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
        params: {
            page,
            pageSize
        }
    });
    return response.data;
}

// Create a new batch configuration
export async function createBatchConfiguration(
    msalInstance: IPublicClientApplication, 
    batchConfig: BatchConfigurationRequest, 
    account?: AccountInfo
): Promise<BatchConfiguration> {
    const token = await getAccessToken(msalInstance, account);
    
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('companyDataSourcesId', batchConfig.companyDataSourcesId.toString());
    formData.append('prompt1', batchConfig.prompt1);
    formData.append('prompt2', batchConfig.prompt2);
    formData.append('prompt3', batchConfig.prompt3);
    formData.append('status', batchConfig.status);
    formData.append('files', batchConfig.file);
    
    const response = await axios.post(`${backendBaseUrl}api/BatchConfiguration`, formData, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
}

// Get a specific batch configuration by ID
export async function getBatchConfiguration(
    msalInstance: IPublicClientApplication, 
    id: number, 
    account?: AccountInfo
): Promise<BatchConfiguration> {
    const token = await getAccessToken(msalInstance, account);
    const response = await axios.get(`${backendBaseUrl}api/BatchConfiguration/${id}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data;
}

// Update a batch configuration
export async function updateBatchConfiguration(
    msalInstance: IPublicClientApplication, 
    id: number,
    batchConfig: BatchConfiguration, 
    account?: AccountInfo
): Promise<BatchConfiguration> {
    const token = await getAccessToken(msalInstance, account);
    const response = await axios.put(`${backendBaseUrl}api/BatchConfiguration/${id}`, batchConfig, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });
    return response.data;
}

// Delete a batch configuration
export async function deleteBatchConfiguration(
    msalInstance: IPublicClientApplication, 
    id: number, 
    account?: AccountInfo
): Promise<void> {
    const token = await getAccessToken(msalInstance, account);
    await axios.delete(`${backendBaseUrl}api/BatchConfiguration/${id}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
}

// Download a batch configuration file
export async function downloadBatchConfigurationFile(
    msalInstance: IPublicClientApplication, 
    id: number, 
    blobFileName: string,
    account?: AccountInfo
): Promise<void> {
    const token = await getAccessToken(msalInstance, account);
    const response = await axios.get(`${backendBaseUrl}api/BatchConfiguration/${id}/download`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
        responseType: 'blob', // Important for file downloads
    });

    // Create blob and download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = blobFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

// Get default templates
export async function getDefaultTemplates(
    msalInstance: IPublicClientApplication, 
    account?: AccountInfo
): Promise<DefaultTemplatesResponse> {
    const token = await getAccessToken(msalInstance, account);
    const response = await axios.get(`${backendBaseUrl}api/BatchConfiguration/default-templates`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data;
}
